import { inject, Injectable } from '@angular/core';
import { CONFIG_TOKEN } from '@pxl/models/common';
import { BrowserHandle, BrowserOptions, BrowserService, LoginService, OnlinePaymentService, PaymentData } from '@pxl/models/contracts';
import { OnlinePaymentAvailabilityModel, OnlinePaymentService as OnlinePaymentBackendService } from '@pxl/shared/generated';
import { from, map, Observable, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

@Injectable()
export class DefaultOnlinePaymentService implements OnlinePaymentService {
    private readonly loginService = inject(LoginService);
    private readonly onlinePaymentBackendService = inject(OnlinePaymentBackendService);
    private readonly browserService = inject(BrowserService);
    private readonly config = inject(CONFIG_TOKEN);
    private readonly cancelCurrentRequest = new Subject<void>();

    private get token(): string {
        return this.loginService.getToken();
    }

    private get apiVersion(): string {
        return this.config.API_VERSION;
    }

    public markAsPaidViaPetsXL(invoiceId: string): Observable<void> {
        return this.onlinePaymentBackendService.markAsPaidViaPetsXL(invoiceId, this.apiVersion, this.token);
    }

    public createPaymentLink(invoiceId: string): Observable<string> {
        return this.onlinePaymentBackendService
            .createPaymentLink(invoiceId, this.apiVersion, this.token)
            .pipe(map(({ checkoutSessionLink }) => checkoutSessionLink));
    }

    public openPaymentLink<T>(
        invoiceId: string,
        browserOpts?: BrowserOptions,
        handleData?: (obs: Observable<PaymentData>) => Observable<T>,
    ): Observable<T | null> {
        this.cancelCurrentRequest.next();
        let browserHandle: BrowserHandle<PaymentData>;
        let initialRequest = this.createPaymentLink(invoiceId).pipe(
            switchMap((paymentLink) => from(this.browserService.open<PaymentData>(paymentLink, browserOpts))),
            take(1),
            tap({
                next: (handle) => {
                    browserHandle = handle;
                },
            }),
            takeUntil(this.cancelCurrentRequest),
            map((handle) => handle.data),
        );
        return ((handleData ? handleData(initialRequest) : initialRequest) as Observable<T | null>).pipe(
            tap({
                complete() {
                    browserHandle?.destroy();
                },
            }),
        );
    }

    public redirectToPaymentLink(invoiceId: string): Observable<unknown> {
        return this.createPaymentLink(invoiceId).pipe(
            switchMap((paymentLink) => from(this.browserService.open<PaymentData>(paymentLink, { windowName: '_self' }))),
            take(1),
        );
    }

    public getAvailability(invoiceId: string): Observable<OnlinePaymentAvailabilityModel> {
        return this.onlinePaymentBackendService.getOnlinePaymentAvailability(invoiceId, this.apiVersion, this.token);
    }
}
